/**
 * PlanCanvas (PRP-WEB-1 W3.2): the SVG body of the floor plan, shared by the read-only
 * `FloorPlanView` (project page thumbnail, estimate page banner) and the full-screen plan page
 * (`V2PlanPage`). Moved here unchanged from FloorPlanView (PRP-SKETCH-FIELD-1/3/5, see that
 * file's header for the drawing rules); a DOM snapshot test proves FloorPlanView's output did
 * not change.
 *
 * Additions, all off unless asked for:
 * - `view`: the view box in plan feet (zoom and pan are a view transform only, `viewBox.ts`).
 *   Default: the plan bounds plus padding, exactly the old thumbnail framing.
 * - `onRoomClick`: room fills become buttons (click, Enter, Space) and every layer above the
 *   fills ignores the pointer, so a click on a wall, label or door lands on its room.
 * - `selectedRoomId`: an accent outline around that room.
 * - `overlay`: drawn last, in plan feet. PRP-WEB-2's drag editor plugs its handles in here.
 * - `underlay`: drawn first, in plan feet (PRP-WEB-2 W2.7: the picture the plan is traced over).
 */
import type { ReactNode, Ref, CSSProperties, KeyboardEvent } from 'react';
import {
  buildWallGraph, openingGlyph, outerOffset, runPts, segmentBands, segmentRuns,
  wallLabels, labelPt, area, DRAW_ORDER, POLICY, DAMAGE_AREAS, damageStrokes, hasArea, wallFtOf, waterLineIn, waterLineLabel,
  type Plan, type Pt, type Room,
} from '@sketch-core';
import { useUiStore } from '../../theme/uiStore';
import { fitViewBox, planMetrics, viewBoxAttr, type ViewBox } from './viewBox';

/** E19 line weights, in plan feet: every wall, and the thin outer face of a thick wall. */
const WALL_LINE = 0.14;
const OUTER_LINE = 0.05;
/** The shared policy's screen block (DD-SF3.18): room fill opacity, wall label offset in font sizes. */
const SCREEN = POLICY.screen;
/** The damage block of the shared policy (colours, sizes, wording). */
const DMG = POLICY.damage;
/** Plan feet per screen pixel for damage strokes, labels and pills: the plan is drawn about this wide. */
const DRAWN_PX = 720;

export const fmtFt = (ft: number): string => {
  const inches = Math.round(ft * 12);
  const f = Math.floor(inches / 12), i = inches - f * 12;
  return i ? `${f}'${i}"` : `${f}'`;
};

const P = (p: Pt) => `${p.x},${p.y}`;
const ln = ([a, b]: [Pt, Pt]) => ({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });

/** Damage on the plan (item 6): damaged wall stretches, their water line and label, CEIL / FLOOR pills. */
function damageLayer(plan: Plan, px: number, fs: number, color: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  const [dash, gap] = DMG.water_line_dash_px;
  for (const r of plan.rooms as Room[]) {
    const wl = waterLineIn(r);
    damageStrokes(r, (vid) => wallFtOf(r, vid)).forEach((st, i) => {
      const key = `${r.id}-${st.vid}-${i}`;
      out.push(
        <line key={`ds${key}`} data-testid="plan-damage-seg" data-room={r.id} data-wall={st.vid} data-implicit={st.implicit ? '1' : '0'}
          x1={st.p0.x} y1={st.p0.y} x2={st.p1.x} y2={st.p1.y} stroke={color}
          strokeWidth={Math.max(st.thickFt * DMG.stroke_t, DMG.stroke_min_px * px)} strokeOpacity={st.implicit ? 0.4 : 0.85} />,
      );
      if (wl == null || (st.toFt - st.fromFt) / px < DMG.label_min_px) return;
      const inset = st.thickFt / 2 + DMG.water_line_inset_px * px;
      const a = { x: st.p0.x - st.n.x * inset, y: st.p0.y - st.n.y * inset };
      const b = { x: st.p1.x - st.n.x * inset, y: st.p1.y - st.n.y * inset };
      out.push(
        <line key={`dw${key}`} data-testid="plan-water-line" data-room={r.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color}
          strokeWidth={1.6 * px} strokeDasharray={`${dash * px} ${gap * px}`} />,
        <text key={`dl${key}`} data-testid="plan-water-label" data-room={r.id} x={(a.x + b.x) / 2 - st.n.x * 10 * px}
          y={(a.y + b.y) / 2 - st.n.y * 10 * px} fontSize={11 * px} fontWeight={700} textAnchor="middle" dominantBaseline="middle"
          fill={color}>{waterLineLabel(wl)}</text>,
      );
    });
    const pills = [
      ...(hasArea(r, DAMAGE_AREAS.ceiling) ? [{ key: 'ceiling', text: DMG.ceiling_icon }] : []),
      ...(hasArea(r, DAMAGE_AREAS.floor) ? [{ key: 'floor', text: DMG.floor_icon }] : []),
    ];
    if (!pills.length) continue;
    const size = DMG.icon_size_px * px, padX = 5 * px, gapX = 4 * px, h = size + 6 * px;
    const widths = pills.map((q) => q.text.length * size * 0.62 + 2 * padX);
    const c = labelPt(r);
    // Above the room name: below it sit the SF line and, on a shared wall, the inside dimension label.
    const y = c.y - fs * 1.1 - h / 2 - fs * 0.3;
    let x = c.x - (widths.reduce((t, w) => t + w, 0) + gapX * (pills.length - 1)) / 2;
    pills.forEach((q, i) => {
      out.push(
        <g key={`di${r.id}-${q.key}`} data-testid={`plan-damage-icon-${q.key}`} data-room={r.id}>
          <rect x={x} y={y - h / 2} width={widths[i]} height={h} rx={h / 2} fill={color} />
          <text x={x + widths[i] / 2} y={y} fontSize={size} fontWeight={700} textAnchor="middle" dominantBaseline="central" fill="#fff">{q.text}</text>
        </g>,
      );
      x += widths[i] + gapX;
    });
  }
  return out;
}

/** The props that make a room fill a button (only when the canvas is interactive). */
function roomHit(r: Room, onRoomClick: (rid: string) => void) {
  return {
    'data-room': r.id,
    role: 'button',
    tabIndex: 0,
    'aria-label': `Room ${r.name}`,
    // The accent selection outline is the visible state; the browser's focus box on an SVG shape is a stray rectangle.
    style: { cursor: 'pointer', outline: 'none' } as CSSProperties,
    onClick: () => onRoomClick(r.id),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRoomClick(r.id); }
    },
  };
}

export interface PlanCanvasProps {
  plan: Plan;
  /** View box in plan feet; default: the whole plan with padding. */
  view?: ViewBox;
  selectedRoomId?: string | null;
  /** Makes the rooms clickable. */
  onRoomClick?: (rid: string) => void;
  /** Drawn last, in plan feet (PRP-WEB-2 handles, the panel's wall highlight). */
  overlay?: ReactNode;
  /** Drawn first, under the rooms, in plan feet (PRP-WEB-2 underlay picture). */
  underlay?: ReactNode;
  className?: string;
  style?: CSSProperties;
  svgRef?: Ref<SVGSVGElement>;
}

export function PlanCanvas({ plan, view, selectedRoomId, onRoomClick, overlay, underlay, className, style, svgRef }: PlanCanvasProps) {
  const theme = useUiStore((st) => st.theme);
  const graph = buildWallGraph(plan);
  const { span, fs } = planMetrics(plan);
  const vb = viewBoxAttr(view ?? fitViewBox(plan));
  const interiorIn = plan.defaults.interiorWallIn;
  const interactive = !!onRoomClick;

  const damage = damageLayer(plan, span / DRAWN_PX, fs, theme === 'dark' ? DMG.color.dark : DMG.color.light);
  const layers: Record<(typeof DRAW_ORDER)[number], JSX.Element[]> = {
    fills: [
      ...plan.rooms.map((r) => (
        <polygon key={`f${r.id}`} data-testid="plan-room-fill" points={r.corners.map(P).join(' ')} fill="var(--tx-faint)" fillOpacity={SCREEN.web_fill_opacity} stroke="none"
          {...(interactive ? roomHit(r, onRoomClick!) : {})} />
      )),
      // E19: the strip between two rooms is room fill, never a dark band (each room's half of the wall).
      ...graph.segments.flatMap((g) => segmentBands(g).map((band, i) => (
        <polygon key={`s${g.id}-${i}`} data-testid="plan-shared-strip" points={band.pts.map(P).join(' ')} fill="var(--tx-faint)" fillOpacity={SCREEN.web_fill_opacity} stroke="none" />
      ))),
    ],
    segments: graph.segments.flatMap((g) => {
      const runs = segmentRuns(g);
      if (g.missing) {
        return runs.map((r, i) => (
          <line key={`m${g.id}-${i}`} data-testid="plan-missing-wall" {...ln(runPts(g, r))}
            stroke="var(--tx-faint)" strokeWidth={0.08} strokeDasharray="0.15 0.3" />
        ));
      }
      const dash = g.known ? undefined : '0.4 0.3';
      const id = g.exterior ? 'plan-wall' : 'plan-shared-wall';
      const o = outerOffset(g);
      const faces = o != null ? [o] : !g.exterior && g.thickIn > interiorIn ? [g.thickFt / 2, -g.thickFt / 2] : [];
      return [
        ...runs.map((r, i) => (
          <line key={`w${g.id}-${i}`} data-testid={id} {...ln(runPts(g, r))}
            stroke="var(--tx-hi)" strokeWidth={WALL_LINE} strokeLinecap="square" strokeDasharray={dash} />
        )),
        ...faces.flatMap((k, j) => runs.map((r, i) => (
          <line key={`o${g.id}-${j}-${i}`} data-testid="plan-wall-outer" {...ln(runPts(g, r, k))}
            stroke="var(--tx-hi)" strokeWidth={OUTER_LINE} strokeLinecap="square" />
        ))),
      ];
    }),
    openings: graph.openings.flatMap((go) => {
      if (go.kind === 'missing' && graph.segments[go.seg]?.missing) return [];
      const gl = openingGlyph(plan, graph.segments, go);
      const key = `${go.rid}:${go.oid}`;
      if (go.kind === 'window') {
        return [(
          <g key={key} data-testid="plan-window">
            {gl.lines.map((l, i) => <line key={i} {...ln(l)} stroke="var(--accent)" strokeWidth={0.08} />)}
          </g>
        )];
      }
      if (go.kind === 'missing') {
        return [(
          <g key={key} data-testid="plan-missing-opening">
            {gl.dotted.map((l, i) => <line key={i} data-testid="plan-missing-wall" {...ln(l)} stroke="var(--tx-faint)" strokeWidth={0.1} strokeDasharray="0.3 0.25" />)}
            {gl.lines.map((l, i) => <line key={`j${i}`} {...ln(l)} stroke="var(--tx-hi)" strokeWidth={0.06} />)}
          </g>
        )];
      }
      return [(
        <g key={key} data-testid="plan-door" data-room={go.rid} data-opening={go.oid}>
          {gl.lines.map((l, i) => <line key={i} {...ln(l)} stroke="var(--tx-hi)" strokeWidth={i < 2 ? 0.06 : 0.1} />)}
          {gl.arcs.map((a, i) => (
            <path key={`a${i}`} data-testid="plan-door-arc" d={`M ${P(a.from)} A ${a.r} ${a.r} 0 0 ${a.sweep} ${P(a.to)}`}
              fill="none" stroke="var(--tx-lo)" strokeWidth={0.06} strokeDasharray="0.25 0.2" />
          ))}
        </g>
      )];
    }),
    labels: [
      ...(damage.length ? [<g key="damage" data-layer="damage" data-testid="plan-damage">{damage}</g>] : []),
      ...wallLabels(plan, graph).filter((l) => l.valueFt >= 2).map((l) => {
        // J19: past the outer face outside; inside its room on a shared wall.
        const k = fs * SCREEN.web_dim_offset_fs;
        const at = { x: l.at.x + l.dir.x * k, y: l.at.y + l.dir.y * k };
        return (
          <text key={`d${l.rid}:${l.vid}`} x={at.x} y={at.y} fontSize={fs * 0.8} textAnchor="middle" dominantBaseline="middle"
            fill="var(--tx-lo)" data-testid="plan-dim" data-room={l.rid} data-inside={l.inside ? '1' : '0'}>{l.known ? '' : '~'}{fmtFt(l.valueFt)}</text>
        );
      }),
      ...plan.rooms.map((r) => {
        const c = labelPt(r);
        return (
          <text key={`n${r.id}`} x={c.x} y={c.y} textAnchor="middle" fill="var(--tx-hi)" data-testid="plan-room-label">
            <tspan x={c.x} fontSize={fs * 1.1} fontWeight={600}>{r.name}</tspan>
            <tspan x={c.x} dy={fs * 1.3} fontSize={fs * 0.9} fill="var(--tx-lo)">{Math.round(Math.abs(area(r)))} SF</tspan>
          </text>
        );
      }),
    ],
  };

  const selected = selectedRoomId ? plan.rooms.find((r) => r.id === selectedRoomId) : undefined;
  return (
    <svg ref={svgRef} viewBox={vb} className={className} style={style}
      role={interactive ? 'group' : 'img'} aria-label={interactive ? 'Floor plan: pick a room to edit it' : 'Floor plan of the captured rooms'}>
      {underlay != null && <g data-layer="underlay" style={NO_POINTER}>{underlay}</g>}
      {DRAW_ORDER.map((k) => (
        <g key={k} data-layer={k} style={interactive && k !== 'fills' ? NO_POINTER : undefined}>{layers[k]}</g>
      ))}
      {selected && (
        <polygon data-testid="plan-room-selected" data-room={selected.id} points={selected.corners.map(P).join(' ')}
          fill="var(--accent)" fillOpacity={0.12} stroke="var(--accent)" strokeWidth={Math.max(0.1, span / 300)} style={NO_POINTER} />
      )}
      {overlay != null && <g data-layer="overlay">{overlay}</g>}
    </svg>
  );
}

const NO_POINTER: CSSProperties = { pointerEvents: 'none' };

export default PlanCanvas;
